import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useMobileMenu } from '../context/MobileMenuContext';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const { mobileMenuOpen, toggleMobileMenu, closeMobileMenu } = useMobileMenu();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation('common');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: `/${language}`, label: t('nav.home') },
    { path: `/${language}/capabilities`, label: t('nav.capabilities') },
    { path: `/${language}/ai-agents`, label: t('nav.aiAgents') },
    { path: `/${language}/ai-governance`, label: t('nav.governance') },
    { path: `/${language}/industries`, label: t('nav.industries') },
    { path: `/${language}/enterprise-ai`, label: t('nav.enterpriseAI') },
    { path: `/${language}/clientele`, label: t('nav.clientele') },
    { path: `/${language}/about`, label: t('nav.about') }
  ];

  return (
    <header className={`site-header ${scrolled ? 'scrolled' : ''}`}>
      <div className="container-fluid">
        <div className="header-content">
          {/* Logo Section */}
          <Link to="/" className="logo-link">
            <img 
              src="/Images/logo-white.png" 
              alt="EWANDS" 
              className={`logo ${theme === 'light' ? 'logo-light-theme' : ''}`}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            {navLinks.map((link, index) => (
              <NavLink 
                key={link.path} 
                to={link.path} 
                end={index === 0}
                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Right Section */}
          <div className="header-right">
            {/* Customer Service Icon */}
            <Link to={`/${language}/contact`} className="customer-service-btn" aria-label="Contact Us">
              <i className="fas fa-location-dot"></i>
            </Link>
            
            {/* Desktop Language & Theme Toggles - Hidden on Mobile */}
            <div className="header-desktop-toggles">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
            
            {/* Mobile Menu Toggle - Next to Icons */}
            <button 
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <i className={`fas fa-${mobileMenuOpen ? 'times' : 'bars'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-nav-inner">
            {navLinks.map((link, index) => (
              <NavLink 
                key={link.path} 
                to={link.path} 
                end={index === 0}
                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                onClick={closeMobileMenu}
              >
                {link.label}
              </NavLink>
            ))}
            
            {/* Mobile Only Link - Contact */}
            <NavLink 
              to={`/${language}/contact`}
              className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
              onClick={closeMobileMenu}
            >
              {t('nav.contact')}
            </NavLink>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
